<template>
    <modal
        :show="show"
        body-classes="p-0"
        modal-classes="modal-md"
        @update:show="$emit('update:show', $event)"
    >
        <card class="card-form mb-0">
            <template slot="header">
                <h2 class="modal-title">Pay Invoice</h2>
                <button
                    type="button"
                    class="close"
                    aria-label="Close"
                    @click="$emit('update:show', false)"
                >
                    <i class="tim-icons icon-simple-remove" />
                </button>
            </template>
            <el-table
                max-height="350"
                :show-summary="invoices.length > 1"
                :summary-method="getSummaries"
                :data="invoices"
            >
                <el-table-column
                    v-for="column in tableColumns"
                    :key="column.label"
                    :width="column.width"
                    :min-width="column.minWidth"
                    :prop="column.prop"
                    :label="column.label"
                    :formatter="column.formatter"
                    :show-overflow-tooltip="true"
                />
            </el-table>
            <div :class="$style.summary">
                <h4>Grand Total</h4>
                <h4>{{ grandTotal }}</h4>
            </div>
            <!--Step 1: Select payment option-->
            <div
                v-if="hasSavedPaymentInfo && paymentOption === null"
                class="text-center"
            >
                <el-button
                    type="primary"
                    class="mt-4"
                    @click="paymentOption = 'saved'"
                >
                    Select a saved bank account
                </el-button>
                <div class="mt-3">or</div>
                <el-button class="mt-3" @click="paymentOption = 'manual'">
                    Enter other payment details
                </el-button>
            </div>
            <div v-if="paymentOption == 'saved'" class="text-center mt-4 mb-4">
                <h5>Select a saved bank account</h5>
                <el-select
                    id="selectPayment"
                    v-model="selectedPaymentMethod"
                    placeholder="Select account"
                >
                    <el-option
                        v-for="pm in savedPaymentInfo"
                        :key="pm.payment_method_id"
                        :label="`${pm.bank_name} ••••${pm.last4}`"
                        :value="pm.payment_method_id"
                    />
                </el-select>
            </div>
            <div
                v-if="
                    clientSecret &&
                    (paymentOption == 'manual' || !hasSavedPaymentInfo)
                "
            >
                <stripe-element-payment
                    v-if="clientSecret !== null"
                    ref="paymentRef"
                    :pk="stripeKey"
                    :confirm-params="confirmParams"
                    :elements-options="elementsOptions"
                />
            </div>
            <div v-if="paymentOption !== null || !hasSavedPaymentInfo">
                <base-button
                    block
                    type="success"
                    class="btn-fill mt-4"
                    @click="pay"
                >
                    Pay {{ grandTotal }}
                </base-button>
                <div class="text-center mt-2">
                    <el-button
                        v-if="paymentOption === 'saved'"
                        type="text"
                        :class="$style.smallFont"
                        @click="paymentOption = 'manual'"
                    >Enter other payment details instead</el-button
                    >
                    <el-button
                        v-if="hasSavedPaymentInfo && paymentOption === 'manual'"
                        type="text"
                        :class="$style.smallFont"
                        @click="paymentOption = 'saved'"
                    >Select a saved bank account instead</el-button
                    >
                </div>
            </div>
        </card>
    </modal>
</template>

<script>
import { Modal } from "src/components/UI";
import { Button } from "element-ui";
import { StripeElementPayment } from "@vue-stripe/vue-stripe";

export default {
    name: "InvoicePayment",
    components: {
        Modal,
        [Button.name]: Button,
        StripeElementPayment,
    },
    props: {
        invoices: {
            type: Array,
            required: true,
        },
        show: {
            type: Boolean,
            required: true,
        },
        showDefaultPaymentOption: {
            type: Boolean,
            required: true,
        },
        savedPaymentInfo: {
            type: Array,
            default: () => [],
        },
        clientSecret: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            paymentOption: null,
            payAfterIntentIsFetched: false,
            selectedPaymentMethod: this.savedPaymentInfo.length
                ? this.savedPaymentInfo[0].payment_method_id
                : null,
            complete: false,
            stripeKey: process.env.VUE_APP_STRIPE_PK_KEY,
            elementsOptions: {
                clientSecret: this.clientSecret,
            },
            confirmParams: {
                // Return URL where the customer should be redirected after the PaymentIntent is confirmed.
                return_url: `${process.env.VUE_APP_URL}/invoices`,
            },
            tableColumns: [
                {
                    prop: "id",
                    label: "Invoice No.",
                },
                {
                    prop: "total",
                    label: "Amount",
                    formatter: (row) => this.formatCurrency(row.total),
                },
                {
                    prop: "interestDue",
                    label: "Interest Applied",
                    formatter: (row) =>
                        this.formatCurrency(this.interestApplied(row)),
                },
            ],
        };
    },
    computed: {
        hasSavedPaymentInfo() {
            return this.savedPaymentInfo.length > 0;
        },
        invoicesSubtotal() {
            return this.invoices
                .map((invoice) => invoice.total)
                .reduce((a, b) => a + b, 0);
        },
        invoicesInterest() {
            return this.invoices
                .map((invoice) => this.interestApplied(invoice))
                .reduce((a, b) => a + b, 0);
        },
        grandTotal() {
            return this.formatCurrency(
                this.invoicesSubtotal + this.invoicesInterest,
            );
        },
    },
    watch: {
        paymentOption(option) {
            if (option === "manual" && !this.clientSecret) {
                this.$emit("getPaymentIntent");
            }
        },
        clientSecret(secret) {
            this.elementsOptions.clientSecret = secret;
        },
    },
    methods: {
        getSummaries() {
            return [
                "Subtotal",
                this.formatCurrency(this.invoicesSubtotal),
                this.formatCurrency(this.invoicesInterest),
            ];
        },
        interestApplied(invoice) {
            if (invoice.applyInterest) {
                return invoice.interestDue;
            }

            return 0;
        },
        pay() {
            if (this.paymentOption === "saved") {
                this.$emit("getPaymentIntent", this.selectedPaymentMethod);
            } else {
                this.$refs.paymentRef.submit();
            }
        },
        formatCurrency(value) {
            const formatter = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
            });
            return formatter.format(value);
        },
    },
};
</script>

<style lang="scss">
#selectPayment:hover {
    color: unset;
}

.el-select .el-input:hover .el-input__icon,
.el-select .el-input:hover input {
    color: unset;
}
</style>

<style module>
.smallFont {
    font-size: 12px;
    font-weight: 500;
}

.summary {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
}

.summary h4:last-of-type {
    color: #666;
}

.row:hover td {
    background: transparent !important;
}

.row td {
    border-top: none !important;
}

.table table {
    margin-bottom: 0;
}

.headerCell {
    padding-bottom: 0px !important;
}
</style>
